// ── Timeline geometry — pure constants and helpers ────────────────────────
// This module is self-contained (no App imports). All exported symbols are
// pure: primitives, fixed arrays, or pure functions that derive from those
// primitives. Nothing here is mutated at runtime.

export const R_START = new Date("1950-01-01");
export const R_END = new Date("2035-07-01");
export const TOTAL_DAYS = Math.ceil((R_END - R_START) / 86400000);
export const dToX = (d, ppd) => ((new Date(d) - R_START) / 86400000) * ppd;
export const AXIS_H = 32,
  // Width of each TSD-chart Y-axis gutter (TsdChart is the only consumer). Sized
  // to hold the widest compact tick label (~6 chars, e.g. "301.9B" / "999.9M";
  // see formatCompactTicks) AND the rotated axis-name label side by side without
  // them overlapping — with a narrower gutter the right-anchored numbers ran back
  // under the vertical title.
  Y_PAD = 72,
  BASE_R = 13,
  LABEL_H = 36,
  STEM_LEN = 12,
  BRANCH_SPACING = 216;
export const TSD_FIX_H = 100;
export const BRANCH_COLORS = ["#44ddcc", "#ff6677", "#aaee44", "#ff9944", "#cc44ff"];
export const SCALE_PRESETS = [
  { label: "YEAR", ppd: 0.6 },
  { label: "MONTH", ppd: 4 },
];
export const LABEL_MIN_PPD = 0.12;

/**
 * Converts viewport client coordinates into `el`'s LOCAL layout coordinates, inverting any CSS
 * `zoom` applied by an ancestor (the app root always carries one; see displayScale.js). Zoom
 * magnifies position:absolute descendants (the crosshair, axis ticks, event geometry, TSD paths)
 * but NOT the raw clientX/clientY a mouse event reports. getBoundingClientRect() is post-zoom
 * (screen px) while offsetWidth is the pre-zoom layout width, so their ratio is the effective
 * zoom; dividing the screen-space offset by it yields the local coordinate the geometry uses.
 */
export const clientToLocal = (el, clientX, clientY) => {
  const r = el.getBoundingClientRect();
  const z = r.width && el.offsetWidth ? r.width / el.offsetWidth : 1;
  return { x: (clientX - r.left) / z, y: (clientY - r.top) / z, width: el.offsetWidth };
};

/** {@link clientToLocal} for the horizontal axis only: `{ x, width }`. */
export const clientToLocalX = (el, clientX) => {
  const { x, width } = clientToLocal(el, clientX, 0);
  return { x, width };
};
/**
 * Whether a trigger's date band is worth drawing at the current zoom.
 *
 * True only when the band would be visible PAST the pie chart drawn on top of it. Below
 * that the wings hide under the circle and the end hashes pinch against its edge, which
 * reads as a smudge on the marker rather than as a window — so the band is dropped and the
 * trigger renders as a plain point. Zoom therefore decides: a trigger gains its band on the
 * way in and loses it on the way out.
 *
 * @param {number} spanPx On-screen width of the window, in px.
 * @param {number} r Radius of THIS trigger's pie (it scales per event), in px.
 * @returns {boolean}
 */
export function bandFitsAroundPie(spanPx, r) {
  // A 1px wing is not visible, so the band must clear the circle by a little on each side
  // rather than merely exceed it.
  const MIN_WING = 3;
  return spanPx >= 2 * r + 2 * MIN_WING;
}

export const isLongRange = (ev) =>
  ev.date_end &&
  ev.date_end !== ev.date &&
  new Date(ev.date_end) - new Date(ev.date) > 30 * 86400000;

export function getTicks(ppd, viewW = 1000, endDate = R_END) {
  const daysVis = viewW / ppd,
    minGap = daysVis / 20,
    t = [];
  const RE = endDate;
  if (minGap <= 1) {
    for (let d = new Date(R_START); d <= RE; d.setDate(d.getDate() + 1)) {
      const f = d.getDate() === 1,
        s = d.getDay() === 0;
      if (f || s)
        t.push({
          x: dToX(d, ppd),
          major: f,
          label: f ? d.toLocaleString("en-US", { month: "short" }) : String(d.getDate()),
        });
    }
  } else if (minGap <= 7) {
    for (let d = new Date(R_START); d <= RE; d.setDate(d.getDate() + 7)) {
      const f = d.getDate() <= 7;
      t.push({
        x: dToX(d, ppd),
        major: f,
        label: f ? d.toLocaleString("en-US", { month: "short" }) : String(d.getDate()),
      });
    }
  } else {
    // Month-and-coarser ticks walk exact UTC month starts. R_START is UTC midnight, so stepping a
    // LOCAL date from it (setMonth/setFullYear) drifted: west of UTC the walk sat on Dec 31 and
    // labelled every year tick with the year before the one it marked (the "2028" tick stood on
    // Jan 1 2029), and day-31 month steps slid the month ticks to the 3rd.
    const y0 = R_START.getUTCFullYear();
    const endMs = RE.getTime();
    const months = (step) => {
      for (let i = 0; ; i += step) {
        const ms = Date.UTC(y0, i, 1);
        if (ms > endMs) break;
        t.push({ d: new Date(ms), ms });
      }
    };
    if (minGap <= 30) months(1);
    else if (minGap <= 180) months(3);
    else if (minGap <= 365) months(12);
    else if (minGap <= 1825) months(60);
    else months(120);
    const monthName = (d) => d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    return t.map(({ d }) => {
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth();
      const x = dToX(d, ppd);
      if (minGap <= 30) return { x, major: m === 0, label: m === 0 ? String(y) : monthName(d) };
      if (minGap <= 180)
        return { x, major: m === 0, label: m === 0 ? String(y) : `Q${Math.floor(m / 3) + 1}` };
      if (minGap <= 365) return { x, major: y % 5 === 0, label: String(y) };
      if (minGap <= 1825) return { x, major: y % 10 === 0, label: String(y) };
      return { x, major: true, label: String(y) };
    });
  }
  return t;
}

// Fine (day + hour) ticks for short-horizon zooms, where getTicks tops out at a
// weekly cadence. Same {x, major, label} shape getTicks returns, so every tick
// consumer (visTicks + the axis render) treats them identically.
//   • DAY scale  (1/6 < minGap ≤ 1): one tick PER DAY — the 1st of the month is a
//     major tick labelled with the month ("Jul", no year), every other day is a
//     minor tick with just the date number. This is what makes "each day" show.
//   • HOUR scale (minGap ≤ 1/6): step laddered 6h→3h→2h→1h as you zoom; midnight
//     is a major tick with the date ("Jul 6"), every other step a minor time ("3 PM").
//
// CRUCIAL: unlike getTicks (which walks the whole 1950–2035 range and lets the
// caller filter), this walks ONLY the visible window (± one screen of margin).
// Hourly across 85 years would be ~745k iterations and rerun on every pan; every
// day would be ~31k; bounded, both are ~20–120 iterations. `viewStartDay` is the
// left edge in days from R_START (App's `viewLeft`) so the loop starts near the view.
export function getFineTicks(ppd, viewStartDay = 0, viewW = 1000, endDate = R_END) {
  const daysVis = viewW / ppd,
    minGap = daysVis / 20;
  // Visible window in ms, one screen of margin each side, clamped to the range —
  // shared by both sub-scales so each stays cheap regardless of zoom.
  // Clamp a negative viewStartDay (panned left of R_START) to 0 so the window
  // stays valid (startMs ≤ endMs) rather than collapsing to an empty range.
  const vsd = Math.max(0, viewStartDay);
  const startMs = R_START.getTime() + Math.max(0, vsd - daysVis) * 86400000;
  const endMs = Math.min(endDate.getTime(), R_START.getTime() + (vsd + 2 * daysVis) * 86400000);
  const t = [];
  // Walk the window in UTC (setUTC*/getUTC* + timeZone:"UTC" labels). dToX measures
  // from R_START, an ISO date-only string parsed as UTC midnight, so the coordinate
  // grid's day/hour boundaries are UTC. Using local setters/getters here would
  // misalign the tick identity (getDate/getHours) and labels with that grid in any
  // non-UTC timezone, and make day/hour x-spacing drift by ±1h across DST
  // transitions (a local "day" is 23h/25h). UTC keeps spacing exactly uniform and
  // positions/labels deterministic regardless of host timezone.
  if (minGap > 1 / 6) {
    // DAY scale: one tick per day, 1st-of-month major (month name, no year).
    const d = new Date(startMs);
    d.setUTCHours(0, 0, 0, 0);
    for (; d.getTime() <= endMs; d.setUTCDate(d.getUTCDate() + 1)) {
      const first = d.getUTCDate() === 1;
      t.push({
        x: dToX(d, ppd),
        major: first,
        label: first
          ? d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })
          : String(d.getUTCDate()),
      });
    }
    return t;
  }
  // HOUR scale: step scales with zoom, mirroring getTicks' day/week/month laddering.
  const stepH = minGap <= 1 / 24 ? 1 : minGap <= 1 / 12 ? 2 : minGap <= 1 / 8 ? 3 : 6;
  const d = new Date(startMs);
  // Floor to the step's hour boundary so ticks sit on clean multiples (0/3/6/…).
  d.setUTCMinutes(0, 0, 0);
  d.setUTCHours(Math.floor(d.getUTCHours() / stepH) * stepH);
  for (; d.getTime() <= endMs; d.setUTCHours(d.getUTCHours() + stepH)) {
    const midnight = d.getUTCHours() === 0;
    t.push({
      x: dToX(d, ppd),
      major: midnight,
      label: midnight
        ? d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
        : d.toLocaleString("en-US", { hour: "numeric", hour12: true, timeZone: "UTC" }),
    });
  }
  return t;
}
// Sub-event scale: a future trigger's dot size grows with its IMPACT. Impact source, in order:
// `dist_impact` (multi-value — total-variation distance the trigger moves the distribution; a
// distribution has no `p_conditional`), then the binary `p_conditional`, then `relevance`, then
// 0.5. Nullish-coalesced so a legitimate 0 (a genuine no-impact value) is respected, not treated
// as absent.
export const evtScale = (ev) =>
  ev.isPast
    ? 0.85
    : 0.14 + Math.min(1, ev.dist_impact ?? ev.p_conditional ?? ev.relevance ?? 0.5) * 1.355;

// The fork node as a branch lane draws it: the branch's own copy of the trigger
// event it forked from, re-anchored to the branch's stored `forkDate`.
//
// `forkDate` is authoritative — it is what the fork corner and the vertical fork
// connector are drawn at — so the node must be anchored to it, NOT recomputed. Two
// cases, and both must end up on forkDate:
//
//   • POINT fork (no window): `date` is overwritten with forkDate. Nothing to
//     preserve, and that is the anchored position the adapter computed.
//   • WINDOWED fork: the window is KEPT (flattening it is what once killed the
//     range, leaving a branched ranged trigger with a single date and no
//     distribution), and `mean_date` is set to forkDate — the top priority in
//     eventAnchorMs — so the circle lands on the corner while date/date_end still
//     drive the bell.
//
// The windowed case used to null mean_date and let eventAnchorMs fall through to the
// window MIDPOINT, on the assumption that the midpoint IS forkDate. It isn't, whenever
// the parent row's anchor came from somewhere else: a collision-slid position (cached
// per-row, and the branch row has no entry of its own) or a server mean_date (nulled
// here). A Sep 1 → Dec 31 trigger slid to Nov 18 forked at Nov 18 but drew its node at
// the Nov 1 midpoint — the node sitting ~17 days LEFT of its own branch corner.
//
// `std_day` stays nulled either way: the bell derives its spread from the raw window.
//
// The anchor is clamped onto the branch's resolution boundary when forkDate lands past
// it (a custom-event fork placed beyond the line, or a stored forkDate a past-resolution
// timeline pushed out) — the SAME clamp the corner applies to fSX. Without it the pair
// diverges again in that case, just at the other end. Pass boundaryMs=Infinity /
// boundaryDate=null (the defaults) for no boundary.
export function buildForkNode(branch, boundaryMs = Infinity, boundaryDate = null) {
  if (!branch || !branch.forkEvent) return branch ? branch.forkEvent : null;
  const fe = { ...branch.forkEvent, mean_date: null, std_day: null };
  const clamp = (d) => (boundaryDate && Date.parse(d) > boundaryMs ? boundaryDate : d);
  const hasWindow = fe.date_end && fe.date_end !== fe.date;
  if (!hasWindow) fe.date = clamp(branch.forkDate || branch.forkEvent.date);
  else if (branch.forkDate) fe.mean_date = clamp(branch.forkDate);
  return fe;
}
